/**
 * The orcaloops member frame (the seam of the UI redesign): a 40px bar over a
 * body of panels either side of the work, in the app's theme, with the
 * keyboard shortcuts dispatched from the root.
 *
 * The frame owns nothing but the theme it reads and the side panels' widths
 * (dragged at each panel's edge, remembered per browser). All three atoms start
 * at the server's defaults so the first client frame matches the server's; the
 * root's callback ref applies the stored values after mount, and an inline
 * pre-paint script stamps the stored theme before hydration. What the bar says, what the
 * left panel lists (navigation on a list page, Layers on a canvas page), what
 * the right panel holds and what the work is all come in as slots, so a route
 * stays the single source of truth for its data. Shortcuts arrive as one
 * `onShortcut(name)` call from a keydown on the root, so a screen handles the
 * ones it has and ignores the rest.
 *
 * Every route that mounts this frame links `orcaloopsStylesheetLinks` from its
 * `head()` (`lib/orcaloops-head.ts`) — the sheet is never imported here.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import { type Atom, useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { type CSSProperties, Fragment, type KeyboardEvent, type ReactNode, useCallback } from "react";

import {
  clampLeftWidth,
  getInitialOrcaloopsLeftWidth,
  LEFT_WIDTH_DEFAULT,
  LEFT_WIDTH_MAX,
  LEFT_WIDTH_MIN,
  orcaloopsLeftWidthAtom,
  persistOrcaloopsLeftWidth,
} from "~/lib/atoms/orcaloops-left-width.ts";
import {
  clampRightWidth,
  getInitialOrcaloopsRightWidth,
  orcaloopsRightWidthAtom,
  persistOrcaloopsRightWidth,
  RIGHT_WIDTH_DEFAULT,
  RIGHT_WIDTH_MAX,
  RIGHT_WIDTH_MIN,
} from "~/lib/atoms/orcaloops-right-width.ts";
import {
  getInitialOrcaloopsTheme,
  nextOrcaloopsTheme,
  orcaloopsThemeAtom,
  persistOrcaloopsTheme,
} from "~/lib/atoms/orcaloops-theme.ts";
import {
  type OrcaloopsShortcut,
  shortcutEventOf,
  shortcutFor,
  type ShortcutTargetLike,
} from "~/lib/orcaloops-shortcuts.ts";

import { Kbd } from "./primitives.tsx";

/** @since 1.0.0 */
export interface OrcaloopsFrameProps {
  /** The work: a list, a canvas, a document. */
  readonly children: ReactNode;
  /**
   * The left panel's content: {@link LeftNav} or a layers list. Omit it for
   * the bar-only frame (a door, a legal page): the body is then the work
   * alone, or the work and the right column.
   */
  readonly left?: ReactNode;
  readonly onShortcut?: (shortcut: OrcaloopsShortcut) => void;
  /** The page's own keys, read after the shortcut table: every keydown on the root. */
  readonly onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  /** The right column, when the screen has one. */
  readonly right?: ReactNode;
  readonly testId?: string;
  /** The bar's content: {@link TopMark}, {@link TopCrumbs}, actions. */
  readonly top: ReactNode;
  /** An extra class on the body, for a page that shapes its columns itself (the v3 run page). */
  readonly bodyClass?: string;
}

/** The DOM target of a keydown as the shortcut table reads it. */
const targetLike = (target: EventTarget | null): ShortcutTargetLike | null =>
  target instanceof HTMLElement ? { tagName: target.tagName, isContentEditable: target.isContentEditable } : null;

/**
 * What the frame does with a keydown: name it, and when it names a shortcut,
 * hand it to the screen. Jump also cancels the browser's own ⌘K.
 *
 * @since 1.0.0
 */
export const frameKeyDown = (
  onShortcut: ((shortcut: OrcaloopsShortcut) => void) | undefined,
  event: KeyboardEvent<HTMLDivElement>,
): void => {
  const shortcut = shortcutFor(shortcutEventOf({
    key: event.key,
    metaKey: event.metaKey,
    ctrlKey: event.ctrlKey,
    altKey: event.altKey,
    target: targetLike(event.target),
  }));
  if (shortcut === null) return;
  if (shortcut === "jump") event.preventDefault();
  onShortcut?.(shortcut);
};

/**
 * Stamps the stored theme on the frame's root before first paint, so a dark
 * browser does not flash the server's light frame while the bundle loads.
 * Resolves exactly as `computeInitialTheme` in `orcaloops-theme.ts`: stored
 * choice, else `prefers-color-scheme`, else light. React does not re-execute
 * inline scripts on hydration, and the root's `suppressHydrationWarning`
 * covers the attribute mismatch. Mirrors `PREPAINT_MODE_SCRIPT` in
 * `routes/clawinstall.tsx`.
 *
 * @since 1.0.0
 */
export const ORCALOOPS_PREPAINT_THEME_SCRIPT = `(function () {
  try {
    var stored = window.localStorage && window.localStorage.getItem("orcaloops-theme");
    var theme = stored === "dark" || stored === "light"
      ? stored
      : (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    document.currentScript.parentElement.setAttribute("data-theme", theme);
  } catch (_) {}
})();`;

/** React's `CSSProperties` widened by the two custom properties the body sets. */
interface BodyStyle extends CSSProperties {
  readonly "--ol-left-w": string;
  readonly "--ol-right-w": string;
}

/** How far one arrow key on the focused grip moves the panel's edge. */
const GRIP_KEY_STEPS: Readonly<Record<string, number>> = { ArrowLeft: -16, ArrowRight: 16 };

/** What a drag handler reads off a pointer event on a grip. */
interface GripDragEvent {
  readonly buttons: number;
  readonly clientX: number;
  readonly currentTarget: { readonly parentElement: Pick<Element, "getBoundingClientRect"> | null };
}

/**
 * The left panel's width under a drag of its grip: the pointer's distance from
 * the body's left edge (where the panel starts), clamped. `null` when no
 * primary button is held — a pointer merely passing over the grip.
 *
 * @since 1.0.0
 */
export const leftGripDragWidth = (event: GripDragEvent): number | null => {
  if ((event.buttons & 1) === 0) return null;
  const bodyLeft = event.currentTarget.parentElement?.getBoundingClientRect().left ?? 0;
  return clampLeftWidth(event.clientX - bodyLeft);
};

/**
 * The right panel's width under a drag of its grip: the body's right edge
 * (where the panel ends) minus the pointer, clamped. `null` when no primary
 * button is held.
 *
 * @since 1.0.0
 */
export const rightGripDragWidth = (event: GripDragEvent): number | null => {
  if ((event.buttons & 1) === 0) return null;
  const bodyRight = event.currentTarget.parentElement?.getBoundingClientRect().right ?? 0;
  return clampRightWidth(bodyRight - event.clientX);
};

/** Which panel a grip resizes, and everything that differs between the two. */
interface GripSide {
  readonly atom: Atom.Writable<number>;
  readonly className: string;
  readonly clamp: (width: number) => number;
  readonly defaultWidth: number;
  readonly dragWidth: (event: GripDragEvent) => number | null;
  readonly label: string;
  readonly max: number;
  readonly min: number;
  readonly persist: (width: number) => void;
  /** An arrow key moves the edge; the right panel grows as its left edge moves left. */
  readonly stepSign: number;
  readonly testId: string;
}

const GRIP_SIDES: Readonly<Record<"left" | "right", GripSide>> = {
  left: {
    atom: orcaloopsLeftWidthAtom,
    className: "ol-grip",
    clamp: clampLeftWidth,
    defaultWidth: LEFT_WIDTH_DEFAULT,
    dragWidth: leftGripDragWidth,
    label: "Resize the left panel",
    max: LEFT_WIDTH_MAX,
    min: LEFT_WIDTH_MIN,
    persist: persistOrcaloopsLeftWidth,
    stepSign: 1,
    testId: "orcaloops-left-grip",
  },
  right: {
    atom: orcaloopsRightWidthAtom,
    className: "ol-grip ol-grip-r",
    clamp: clampRightWidth,
    defaultWidth: RIGHT_WIDTH_DEFAULT,
    dragWidth: rightGripDragWidth,
    label: "Resize the right panel",
    max: RIGHT_WIDTH_MAX,
    min: RIGHT_WIDTH_MIN,
    persist: persistOrcaloopsRightWidth,
    stepSign: -1,
    testId: "orcaloops-right-grip",
  },
};

/** @since 1.0.0 */
export interface PanelGripProps {
  /** The panel the grip resizes: the left one (or the run page's Layers column) or the right one. */
  readonly side: "left" | "right";
  /** The panel's current width, from that side's atom. */
  readonly width: number;
}

/**
 * A panel's edge: drag it (pointer capture, so the drag follows the pointer
 * off the grip without document listeners), step it with the arrow keys,
 * double-click it back to the default. The width is kept on release. It sits
 * on a non-scrolling parent whose edge the drag is measured from.
 *
 * @since 1.0.0
 */
export function PanelGrip({ side, width }: PanelGripProps): ReactNode {
  const grip = GRIP_SIDES[side];
  const setWidth = useAtomSet(grip.atom);
  return (
    <div
      aria-label={grip.label}
      aria-orientation="vertical"
      aria-valuemax={grip.max}
      aria-valuemin={grip.min}
      aria-valuenow={width}
      className={grip.className}
      data-testid={grip.testId}
      onDoubleClick={() => {
        setWidth(grip.defaultWidth);
        grip.persist(grip.defaultWidth);
      }}
      onKeyDown={(event) => {
        const step = GRIP_KEY_STEPS[event.key];
        if (step === undefined) return;
        event.preventDefault();
        setWidth(grip.clamp(width + step * grip.stepSign));
      }}
      onKeyUp={(event) => {
        if (GRIP_KEY_STEPS[event.key] !== undefined) grip.persist(width);
      }}
      onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
      onPointerMove={(event) => {
        const next = grip.dragWidth(event);
        if (next !== null) setWidth(next);
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        grip.persist(width);
      }}
      role="separator"
      tabIndex={0}
    />
  );
}

/** Append an optional class. @since 1.0.0 */
export const withClass = (base: string, extra: string | undefined): string =>
  extra === undefined ? base : `${base} ${extra}`;

/**
 * The frame. See {@link OrcaloopsFrameProps}.
 *
 * @since 1.0.0
 */
export function OrcaloopsFrame(
  { bodyClass, children, left, onKeyDown, onShortcut, right, testId = "orcaloops-frame", top }: OrcaloopsFrameProps,
): ReactNode {
  const theme = useAtomValue(orcaloopsThemeAtom);
  const leftWidth = useAtomValue(orcaloopsLeftWidthAtom);
  const rightWidth = useAtomValue(orcaloopsRightWidthAtom);
  const setTheme = useAtomSet(orcaloopsThemeAtom);
  const setLeftWidth = useAtomSet(orcaloopsLeftWidthAtom);
  const setRightWidth = useAtomSet(orcaloopsRightWidthAtom);
  // Refs never run on the server: once per mount, apply the stored theme and
  // widths over the defaults the server (and the first client frame) rendered.
  const applyStored = useCallback((element: HTMLDivElement | null) => {
    if (element === null) return;
    setTheme(getInitialOrcaloopsTheme());
    setLeftWidth(getInitialOrcaloopsLeftWidth());
    setRightWidth(getInitialOrcaloopsRightWidth());
  }, [setTheme, setLeftWidth, setRightWidth]);
  const bodyStyle: BodyStyle = { "--ol-left-w": `${leftWidth}px`, "--ol-right-w": `${rightWidth}px` };
  const rightPanel = right === undefined ? null : (
    <>
      <PanelGrip side="right" width={rightWidth} />
      <aside className="ol-right" data-testid="orcaloops-right">{right}</aside>
    </>
  );
  return (
    <div
      className="ol-app"
      data-testid={testId}
      data-theme={theme}
      onKeyDown={(event) => {
        frameKeyDown(onShortcut, event);
        onKeyDown?.(event);
      }}
      ref={applyStored}
      suppressHydrationWarning
      tabIndex={-1}
    >
      <script dangerouslySetInnerHTML={{ __html: ORCALOOPS_PREPAINT_THEME_SCRIPT }} />
      <div className="ol-top" data-testid="orcaloops-top">{top}</div>
      {left === undefined
        ? (
          <div
            className={withClass(right === undefined ? "ol-body ol-one" : "ol-body ol-one ol-two", bodyClass)}
            style={bodyStyle}
          >
            <div className="ol-main" data-testid="orcaloops-main">{children}</div>
            {rightPanel}
          </div>
        )
        : (
          <div className={withClass(right === undefined ? "ol-body" : "ol-body ol-three", bodyClass)} style={bodyStyle}>
            <aside className="ol-left" data-testid="orcaloops-left">{left}</aside>
            <PanelGrip side="left" width={leftWidth} />
            <div className="ol-main" data-testid="orcaloops-main">{children}</div>
            {rightPanel}
          </div>
        )}
    </div>
  );
}

/** The orcaloops mark at the bar's left edge. @since 1.0.0 */
export const TopMark = (): ReactNode => (
  <span aria-hidden="true" className="ol-mark">
    <svg
      fill="none"
      height="11"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 16 16"
      width="11"
    >
      <path d="M12.5 8a4.5 4.5 0 1 1-1.3-3.2" />
      <path d="M12.5 3.5v2.5h-2.5" />
    </svg>
  </span>
);

/** One breadcrumb: a link when it has an `href`, the current page when it has none. @since 1.0.0 */
export interface Crumb {
  readonly href?: string;
  readonly label: string;
}

/** The bar's breadcrumb: workspace, then the path, slash-separated. @since 1.0.0 */
export const TopCrumbs = (
  { crumbs, tail }: { readonly crumbs: ReadonlyArray<Crumb>; readonly tail?: ReactNode },
): ReactNode => (
  <div className="ol-crumb" data-testid="orcaloops-crumbs">
    {crumbs.map((crumb, index) => (
      <Fragment key={`${index}-${crumb.label}`}>
        {index === 0 ? null : <span className="ol-sl">/</span>}
        {crumb.href === undefined
          ? <span className="ol-c ol-cur">{crumb.label}</span>
          : <a href={crumb.href}>{crumb.label}</a>}
      </Fragment>
    ))}
    {tail}
  </div>
);

/** The tones a bar action can take: ghost, the primary (`go`), and the amber primary for a run waiting on you. @since 1.0.0 */
export type TopActionTone = "ghost" | "go" | "wait";

/** @since 1.0.0 */
export interface TopActionProps {
  readonly ariaLabel?: string;
  readonly children?: ReactNode;
  readonly danger?: boolean;
  readonly href?: string;
  /** The key printed beside the action. */
  readonly kbd?: string;
  readonly on?: boolean;
  readonly onClick?: () => void;
  readonly testId?: string;
  readonly tone?: TopActionTone;
}

const topActionClass = (tone: TopActionTone, on: boolean, danger: boolean): string =>
  tone === "ghost"
    ? ["ol-tb", on ? "ol-on" : null, danger ? "ol-bad" : null].filter((part) => part !== null).join(" ")
    : tone === "go"
    ? "ol-go"
    : "ol-go ol-wait";

/** An action in the bar. @since 1.0.0 */
export const TopAction = (
  { ariaLabel, children, danger = false, href, kbd, on = false, onClick, testId, tone = "ghost" }: TopActionProps,
): ReactNode => {
  const className = topActionClass(tone, on, danger);
  const body = (
    <>
      {children}
      {kbd === undefined ? null : <Kbd>{kbd}</Kbd>}
    </>
  );
  return href === undefined
    ? (
      <button aria-label={ariaLabel} className={className} data-testid={testId} onClick={onClick} type="button">
        {body}
      </button>
    )
    : (
      <a aria-label={ariaLabel} className={className} data-testid={testId} href={href} onClick={onClick}>
        {body}
      </a>
    );
};

/** Pushes what follows to the bar's right edge. @since 1.0.0 */
export const TopGrow = (): ReactNode => <div className="ol-grow" />;

/** A hairline between groups of bar actions. @since 1.0.0 */
export const TopSep = (): ReactNode => <span aria-hidden="true" className="ol-sep" />;

/** The stacked avatars of who is looking at this screen. @since 1.0.0 */
export const TopWho = (
  { people }: {
    readonly people: ReadonlyArray<{ readonly color: string; readonly initials: string; readonly name: string }>;
  },
): ReactNode => (
  <span className="ol-who" data-testid="orcaloops-who">
    {people.map((person) => (
      <span
        aria-label={person.name}
        className="ol-av"
        key={person.name}
        role="img"
        style={{ background: person.color }}
      >
        {person.initials}
      </span>
    ))}
  </span>
);

/**
 * The light / dark toggle: flips {@link orcaloopsThemeAtom} and keeps the
 * choice for the next visit.
 *
 * @since 1.0.0
 */
export function ThemeToggle(): ReactNode {
  const theme = useAtomValue(orcaloopsThemeAtom);
  const setTheme = useAtomSet(orcaloopsThemeAtom);
  const next = nextOrcaloopsTheme(theme);
  return (
    <button
      aria-label={`Switch to ${next} theme`}
      className="ol-tb"
      data-testid="orcaloops-theme-toggle"
      onClick={() => {
        setTheme(next);
        persistOrcaloopsTheme(next);
      }}
      type="button"
    >
      <svg
        fill="none"
        height="14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        viewBox="0 0 16 16"
        width="14"
      >
        {theme === "dark"
          ? (
            <path d="M8 2v1.5M8 12.5V14M2 8h1.5M12.5 8H14M3.8 3.8l1 1M11.2 11.2l1 1M3.8 12.2l1-1M11.2 4.8l1-1M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
          )
          : <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" />}
      </svg>
    </button>
  );
}
